import {Component, OnInit, ViewChild} from '@angular/core';
import {COMMA, ENTER, TAB} from '@angular/cdk/keycodes';

import {forkJoin, Observable, of, Subject, Subscription, timer} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';

import {ApplicationConstant} from 'src/app/utilities/ApplicationConstant';
import {DeleteGroupsDialogComponent} from './groups-delete/groups-delete.component';
import {ScheduleUpdateComponent} from './schedule/schedule-update/schedule-update.component';
import {AutocompleteComponent, ButtonConfig, ChipPopupComponent, FilterConfig} from 'src/app/components';
import {FaqComponent, GroupTypeWarningDialogComponent} from 'src/app/screens';
import {ScheduleCreateComponent} from './schedule/schedule-create/schedule-create.component';
import {Activity, Device, Group, GroupType, Individual, Pageable} from 'src/app/models';
import {DataService, DevicesService, GroupsService, GroupTypeService, IndividualsService, SchedulesService, SnackbarService} from 'src/app/services';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatDialog} from '@angular/material/dialog';

@Component({
    selector: 'app-members',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss']
})
export class GroupsComponent implements OnInit {
    groupTypes: GroupType[] = [];
    separatorKeysCodes: number[] = [ENTER, COMMA, TAB];
    selectedGroup: Group;
    groupCheckedRadioEsc = '';
    groupCheckedRadioNotif = '';
    escalationRadioButton = '';
    notificationRadioButton = '';
    displayId = false;
    checkmark = 'uncheck';
    escalateError = '';
    escalateToChipList = [];
    // autocomplete component stuff
    filter = '';
    pageNum = 1;
    // user info
    isAdmin = false;
    isOwner = false;
    isCurrentMember = false;
    isEscalateTo = false;
    currentUser: any;
    currentUserInfo: any;
    // autocomplete arrays
    autocompleteEscalate: any[] = [];
    autocompleteGroups: Group[] = [];
    autocompleteGroupsFormatted: string[] = [];
    autocompleteGroupOwners: Individual[] = [];
    // autocomplete observables
    escalateToChange = new Subject<string>();
    groupChange = new Subject<string>();
    groupOwnerChange = new Subject<string>();
    // autocomplete searches
    currentEscalation = '';
    currentGroupOwner = '';
    groupSearch;
    // validity
    escalateTimeValid = true;
    reminderTimeValid = true;
    ackRequiredValid = true;
    escalateValid = true;
    groupLevelValid = true;
    groupNameValid = true;
    groupOwnersValid = true;
    groupTypeValid = true;
    ready = true;
    hourglass = false;
    viewInfo = true;
    fullReady = false;
    groupTypeUsesSystemOverride = false;
    @ViewChild('autoComplete', {static: true}) autoComplete: AutocompleteComponent;
    buttonConfig: ButtonConfig;
    filterConfig: FilterConfig;
    members: any;
    unregisteredUser = false;
    prevGroupTypeId: number;
    defaultEscalationInterval: number;
    defaultReminderInterval: number;

    // notification
    notificationToChipList = [];
    autocompleteNotificationTo = [];
    currentNotificationTo = '';
    notificationToChange = new Subject<string>();
    isServiceAccount = false;

    // controls for screen and button behavior
    isProcessing = false;
    showDetailsPage = false;
    showSpinner = false;
    saveButtonDisabled = true;
    showAckRequired = false;

    //Grid component
    header = ['ID', 'Name'];
    width = [50, 9999];
    groupList: Group[] = [];
    onlyShowOwnedGroups = true;
    showGroupFilter = false;
    page = {
        limit: 20,
        count: 0,
        offset: 0,
        orderBy: 'name',
        orderDir: 'asc'
    };
    oldPage = 0;

    constructor(private dialog: MatDialog,
                private dataService: DataService,
                private individualsService: IndividualsService,
                private scheduleService: SchedulesService,
                public deviceService: DevicesService,
                public groupTypeService: GroupTypeService,
                public groupsService: GroupsService,
                private snackbarService: SnackbarService) {
    }

    private static isValidTimeLimit(tempValue: number, event: any) {
        // eslint-disable-next-line radix
        const min = parseInt(event.target.min);
        // eslint-disable-next-line radix
        const max = parseInt(event.target.max);
        return (tempValue >= min && tempValue <= max);
    }

    ngOnInit() {
        this.setupOnChangeSearches();
        let groupTypesNames: string[] = ['All'];
        this.groupTypeService.getGroupTypes().subscribe(groupType => {
            for (let gType of groupType) {
                groupTypesNames.push(gType.group_type);
            }
        });

        this.filterConfig = {
            enable: true,
            filterOptions: groupTypesNames,
            defaultOption: 'All',
            filterHint: 'Select a Group Type'
        };

        this.buttonConfig = {
            enable: true,
            icon: 'add_circle',
            label: 'Add Icon',
            color: 'primary',
            id: 'GroupAdd',
            class: 'flex'
        };

        // schedule feature flag
        this.groupTypeService.getGroupTypes().subscribe((gTypes: GroupType[]) => this.groupTypes = gTypes);
        this.dataService.getUserInfo().subscribe(res => {
            this.currentUserInfo = res;
            this.isAdmin = this.currentUserInfo.isAdmin;

            if ((this.currentUserInfo['email']).startsWith('svc')) {
                this.isServiceAccount = true;
            }

            this.individualsService.getIndividuals(this.currentUserInfo['email']).subscribe({
                next: individual => {
                    this.currentUser = individual[0] || null;
                    if (!this.currentUser) {
                        this.unregisteredUser = true;
                    }
                    this.fullReady = true;
                }, error: () => {
                    this.unregisteredUser = true;
                }
            }).add(() => this.getGroupsForGrid());
        });

        this.dataService.getSetting('ESCALATION_INTERVAL').subscribe((esc_int: number) => this.defaultEscalationInterval = esc_int);
        this.dataService.getSetting('REMINDER_INTERVAL').subscribe((rem_int: number) => this.defaultReminderInterval = rem_int);
    }

    check() {
        this.checkmark = 'check';
        timer(5000).subscribe(() => {
            if (this.checkmark === 'check') {
                this.checkmark = 'out';
            }
        });
    }

    selectGroup(event: MatAutocompleteSelectedEvent) {
        this.resetVariablesOnSelect();
        const selection = this.autocompleteGroups.find(e => e.name === event.option.value);
        this.groupsService.getGroup(selection.id).subscribe({
            next: this.populateGroup.bind(this),
            error: () => {
                this.snackbarService.openSnackBar('Error retrieving group from server, try again later. If this persists, contact an admin', true);
                this.ready = true;
                this.isProcessing = false;
                this.showSpinner = false;
                return;
            }
        });
        this.autocompleteGroupsFormatted = [];
        this.autocompleteGroups = [];
        this.saveButtonDisabled = true;
    }

    newGroup() {
        if (this.unregisteredUser) {
            this.snackbarService.openSnackBar(`Please register in Alerting by saving your user on the User Profile page to create a group.`, true);
        } else {
            this.isProcessing = true;
            this.showDetailsPage = false;
            this.showSpinner = true;
            this.viewInfo = true;
            this.showAckRequired = false;
            const emptySearchString = this.groupSearch === undefined;
            const groupNameExists = (!emptySearchString && this.groupSearch.trim() !== '' ? this.autocompleteGroups.find(grp => grp.name === this.groupSearch) : false);

            if (groupNameExists) {
                this.snackbarService.openSnackBar(`Group named ${groupNameExists.name} already exists.`, true);
                this.groupsService.getGroup(groupNameExists.id).subscribe(e => this.populateGroup(e));
                this.isProcessing = false;
                this.showSpinner = false;
            } else {
                this.selectedGroup = new Group();
                this.selectedGroup.name = (!emptySearchString ? this.groupSearch : '');
                this.selectedGroup.generateDeviceName();
                this.addGroupOwner(this.currentUser);
                this.populateGroup(this.selectedGroup);
                this.groupSearch = '';
                this.autoComplete.clear();
                this.saveButtonDisabled = true;
            }
            this.onGroupChanged('');
        }
    }

    populateGroup(group?: Group) {
        this.selectedGroup = group ? new Group(group) : new Group();

        if (this.selectedGroup.device_name == null) {
            this.selectedGroup.generateDeviceName();
        }
        this.escalateToChipList = [this.selectedGroup.escalate_to];

        if (this.selectedGroup.escalate_to_type === ApplicationConstant.individual || this.selectedGroup.escalate_to_type === ApplicationConstant.device) {
            this.escalationRadioButton = ApplicationConstant.individual;
        } else if (this.selectedGroup.escalate_to_type === ApplicationConstant.group) {
            this.escalationRadioButton = ApplicationConstant.group;
        } else {
            this.escalationRadioButton = ApplicationConstant.doNotEscalate;
            this.selectedGroup.escalate_time_limit = this.defaultEscalationInterval;
            this.selectedGroup.reminder_time_limit = this.defaultReminderInterval;
        }

        if (this.selectedGroup.notification_to_type === ApplicationConstant.individual || this.selectedGroup.notification_to_type === ApplicationConstant.device) {
            this.notificationRadioButton = ApplicationConstant.individual;
        } else if (this.selectedGroup.notification_to_type === ApplicationConstant.group) {
            this.notificationRadioButton = ApplicationConstant.group;
        } else {
            this.notificationRadioButton = ApplicationConstant.doNotNotify;
        }

        if (this.currentUser) {
            this.isCurrentMember = !!this.selectedGroup.members.find(mem => mem.name === this.currentUser.full_name);
            this.isOwner = !!this.selectedGroup.owners.find(mem => mem.full_name === this.currentUser.full_name);
            this.isEscalateTo = this.selectedGroup.escalate_to === this.currentUser.full_name;
        } else {
            this.isCurrentMember = false;
            this.isEscalateTo = false;
            this.isOwner = false;
        }

        this.groupMembersToRecipients();
        this.resetValidity();

        for (let gType of this.groupTypes) {
            if (gType.group_type_id === this.selectedGroup.group_type_id) {
                if (gType.use_system_global_intervals) {
                    this.groupTypeUsesSystemOverride = true;
                    this.selectedGroup.escalate_time_limit = this.defaultEscalationInterval;
                    this.selectedGroup.reminder_time_limit = this.defaultReminderInterval;
                } else {
                    this.groupTypeUsesSystemOverride = false;
                }
                break;
            }
        }

        this.getNotify();
        if (this.selectedGroup.id) {
            this.groupsService.getMembership(this.selectedGroup.id).subscribe({
                next: members => this.members = members,
                error: () => this.members = []
            }).add(() => {
                this.ready = true;
            });
        } else {
            this.ready = true;
        }
        this.isProcessing = false;
        this.showDetailsPage = true;
        this.showSpinner = false;
        this.showAckRequired = this.selectedGroup.ack_required;
        this.groupCheckedRadioEsc = this.selectedGroup.escalate_to_type;
        this.groupCheckedRadioNotif = this.selectedGroup.notification_to_type;
        this.prevGroupTypeId = this.selectedGroup.group_type_id;
    }

    resetValidity() {
        this.groupOwnersValid = true;
        this.escalateTimeValid = true;
        this.reminderTimeValid = true;
        this.groupTypeValid = true;
        this.escalateValid = true;
        this.groupNameValid = true;
    }

    isValid(): Observable<any> {
        this.saveButtonDisabled = false;
        const escalateObservable = new Observable((observer) => {
            let isMemberObserver: Subscription;

            // current escalation will be empty if group is doNotEscalate
            if (this.selectedGroup.escalate_to_type !== ApplicationConstant.doNotEscalate || this.currentEscalation !== '' || this.selectedGroup.escalate_to !== ApplicationConstant.doNotEscalate) {
                this.escalateValid = false;
                if (!this.escalateToChipList.length) {
                    this.escalateError = 'empty.';
                } else if (this.selectedGroup.name === this.escalateToChipList[0]) {
                    this.escalateError = 'the group name.';
                } else if (this.selectedGroup.members.find(m => m.name === this.escalateToChipList[0])) {
                    this.escalateError = 'a member.';
                } else if (this.selectedGroup.sub_group_enabled) {
                    // Must wait on this async call before responding to caller (i.e. observer.next)
                    isMemberObserver = this.isEscalateToAMember().subscribe(isMember => {
                        isMember ? this.escalateError = 'a member of a subgroup.' : this.escalateValid = true;
                        observer.next(this.escalateValid);
                        observer.complete();
                    });
                } else {
                    this.escalateValid = true;
                }
            }

            // If NOT waiting on the isMemberObserver, then return immediately
            if (!isMemberObserver) {
                observer.next(this.escalateValid);
                observer.complete();
            }
        });
        if (this.selectedGroup.escalate_to !== ApplicationConstant.doNotEscalate) {
            this.escalateTimeValid = (this.selectedGroup.escalate_time_limit !== null && this.selectedGroup.escalate_time_limit !== undefined);

            this.reminderTimeValid = (this.selectedGroup.reminder_time_limit !== null && this.selectedGroup.reminder_time_limit !== undefined);
        } else {
            this.escalateTimeValid = true;
            this.reminderTimeValid = true;
        }

        const pattern = /^(?=.*\S).+$/g;
        this.groupNameValid = (this.selectedGroup.name && this.selectedGroup.name.length !== 0 && pattern.test(this.selectedGroup.name));
        this.groupOwnersValid = (this.selectedGroup.owners && this.selectedGroup.owners.length !== 0);
        this.groupTypeValid = (this.selectedGroup.group_type_id !== null);
        // all must return true to continue
        return new Observable((observer) => {
            escalateObservable.subscribe(escalateValid => {
                observer.next(escalateValid &&
                    this.escalateTimeValid &&
                    this.reminderTimeValid &&
                    this.groupOwnersValid &&
                    this.groupNameValid &&
                    this.groupTypeValid &&
                    this.groupLevelValid);
                observer.complete();
            });
        });
    }

    isEscalateToAMember(): Observable<any> {
        return new Observable((observer) => {
            const multiChecks = [];
            // Build list of subgroup members to check
            this.selectedGroup.members.forEach((member: any) => {
                const id = member.recipient_id || member.member_id;
                multiChecks.push(this.groupsService.isGroupMember(id, this.selectedGroup.escalate_to_id, this.selectedGroup.escalate_to_type, true));
            });

            // Send check requests and wait for them all
            forkJoin(multiChecks).subscribe(results => {
                // Are any of them true?
                observer.next(results.includes(true));
                observer.complete();
            });
            // if never entered forkJoin
            observer.next(false);
            observer.complete();
        });
    }

    onTimeLimitKeyPress(event: any) {
        // noinspection JSDeprecatedSymbols
        if (event.charCode >= 48 && event.charCode <= 57) {
            // eslint-disable-next-line radix
            const tempValue = parseInt(event.target.value + event.key);
            return GroupsComponent.isValidTimeLimit(tempValue, event);
        }
        return false;
    }

    onTimeLimitPaste(event: any) {
        const clipboardData = event.clipboardData || event.originalEvent.clipboardData;
        const pastedText = clipboardData.getData('text');

        // eslint-disable-next-line radix
        const tempValue = parseInt(pastedText) || 99999;
        return GroupsComponent.isValidTimeLimit(tempValue, event);
    }

    updatePopup() {
        this.scheduleService.getScheduleByGroupID(this.selectedGroup.id).subscribe(schedule => {
            const popupDialog = schedule.length === 0 ? this.dialog.open(ScheduleCreateComponent, {
                disableClose: true,
                width: '80%',
                data: {dataKey: this.selectedGroup}
            }) : this.dialog.open(ScheduleUpdateComponent, {
                disableClose: true,
                width: '80%',
                data: {dataKey: this.selectedGroup, schedule: schedule}
            });

            popupDialog.afterClosed().subscribe(statement => {
                if (statement) {
                    this.groupsService.getGroup(this.selectedGroup.id).subscribe(this.populateGroup.bind(this));
                }
            });
        });
    }

    saveGroup() {
        this.isProcessing = true;
        // Prep recipients for GroupMember update
        this.autocompleteGroupsFormatted = [];
        let specificError = false;

        this.isValid().subscribe(valid => {
            this.recipientsToGroupMembers();
            if (this.selectedGroup.sub_group_enabled) {
                this.selectedGroup.open_group = false; // turn off self sub if subgroups
                this.selectedGroup.members.forEach(mem => {
                    if (mem.member_type === 'INDIVIDUAL' || mem.member_type === 'DEVICE') {
                        this.selectedGroup.members = this.selectedGroup.members.filter(a => a !== mem);
                    } else if (this.selectedGroup.id === mem.member_id) {
                        specificError = true;
                        this.snackbarService.openSnackBar(`Cannot add ${mem.name} as a member of itself`, true);
                        valid = false;
                        this.isProcessing = false;
                        this.showSpinner = false;
                    }
                });
            } else {
                this.selectedGroup.members.forEach(mem => {
                    if (mem.member_type === 'GROUP') {
                        this.selectedGroup.members = this.selectedGroup.members.filter(a => a !== mem);
                    }
                });
            }
            if (!this.selectedGroup.members.length) {
                specificError = true;
                this.snackbarService.openSnackBar(`Please select at least one ${this.selectedGroup.sub_group_enabled ? 'subgroup' : 'individual member'}.`, true);
                valid = false;
                this.isProcessing = false;
                this.showSpinner = false;
            }
            this.deviceService.getDevicesByName(this.selectedGroup.name).subscribe(devices => {
                devices.forEach((device) => {
                    if (device && device.name.toLowerCase() === this.selectedGroup.name.toLowerCase()) {
                        specificError = true;
                        this.snackbarService.openSnackBar(`Cannot create device ${this.selectedGroup.name}, device named ${this.selectedGroup.name} already exists`, true);
                        valid = false;
                        this.isProcessing = false;
                        this.showSpinner = false;
                    }
                });

                if (valid && (this.isAdmin || this.isOwner)) {
                    const groupRequest = new Group(this.selectedGroup);
                    groupRequest.name = groupRequest.name.trim();
                    groupRequest.generateDeviceName();
                    groupRequest.active = true;
                    groupRequest.escalate_to = this.escalateToChipList[0];
                    if (this.groupTypeUsesSystemOverride) {
                        groupRequest.escalate_time_limit = null;
                        groupRequest.reminder_time_limit = null;
                    }
                    groupRequest.id ? this.updateGroup(groupRequest) : this.addGroup(groupRequest);
                } else {
                    if (!specificError) {
                        if (!this.escalateValid) {
                            let escalate_to = this.escalateToChipList.length > 1 ? this.selectedGroup.escalate_to : 'the escalation';
                            this.snackbarService.openSnackBar(`Cannot use ${escalate_to} because it is ${this.escalateError}`, true);
                        } else {
                            this.snackbarService.openSnackBar(` Cannot Save ${this.selectedGroup.name} due to missing values indicated below`, true);
                        }
                    }
                }
                this.isProcessing = false;
                this.showSpinner = false;
            });
        });

        this.getGroupsForGrid();
    }

    addCurrentUser() {
        this.groupsService.addGroupMember(this.selectedGroup.id, this.currentUser.external_id, false).subscribe((group: Group) => {
            this.snackbarService.openSnackBar(`Successfully added you to ${this.selectedGroup.name}.`, false);
            this.saveButtonDisabled = false;
            this.populateGroup(group);
        });
    }

    removeCurrentUser() {
        this.groupsService.removeGroupMember(this.selectedGroup.id, this.currentUser.external_id).subscribe((group: Group) => {
            this.snackbarService.openSnackBar(`Successfully removed you from ${this.selectedGroup.name}.`, false);
            this.saveButtonDisabled = false;
            this.populateGroup(group);
        });
    }

    activateGroup() {
        this.selectedGroup.active = !this.selectedGroup.active;
        this.saveButtonDisabled = false;
    }

    deleteGroup() {
        this.isProcessing = true;
        const popupDialog = this.dialog.open(DeleteGroupsDialogComponent, {
            width: '325px',
            height: '205px',
            panelClass: 'confirmation',
            data: {
                dataKey: this.selectedGroup.name, dialogTitle: 'Delete Current Group?'
            }
        });
        popupDialog.afterClosed().subscribe(result => {
            this.hourglass = true;
            if (result) {
                this.groupsService.deleteGroup(this.selectedGroup.id).subscribe({
                    next: () => {
                        this.snackbarService.openSnackBar('Group Deleted Successfully', false);
                        this.cancel();
                        this.selectedGroup = undefined;
                    }, error: () => this.dataFetchError('Group deletion ERROR')
                }).add(() => {
                    this.isProcessing = false;
                    this.hourglass = false;
                });
            } else {
                this.isProcessing = false;
                this.hourglass = false;
            }
        });
    }

    closeGroup() {
        this.selectedGroup = null;
        this.showDetailsPage = false;
    }

    doGroupUpdates(request: Group, method: string, doneMessage: string) {
        this.groupsService[method](request).subscribe({
            next: group => {
                // after successful creation of the group we reload the screen
                this.groupsService.getGroup(group.id).subscribe({
                    next: (refreshedGroup) => {
                        this.populateGroup(refreshedGroup);
                        this.snackbarService.openSnackBar(doneMessage, false);
                    }, error: () => this.snackbarService.openSnackBar('Error refreshing group', true)
                }).add(() => {
                    this.isProcessing = false;
                    this.showSpinner = false;
                });
            }, error: err => {
                if (err.error.message.includes('device_name conflict')) {
                    this.dataFetchError('Group name conflicts with existing channel, group, or individual name');
                } else {
                    this.dataFetchError('Group could not be added or updated');
                }
            }, complete: () => {
                this.isProcessing = false;
                this.showSpinner = false;
                this.saveButtonDisabled = true;
                this.onlyShowOwnedGroups = true;
            }
        });
    }

    /**
     * Initialize search Subject(s) with user input wait period.
     * Call search after wait period expires without additional user input.
     *
     */
    setupOnChangeSearches() {
        const delay = 750;
        this.groupChange.pipe(debounceTime(delay), distinctUntilChanged())
            .subscribe((term: string) => this.getAutocompleteGroups(term.toString().trim()));

        this.escalateToChange.pipe(debounceTime(delay), distinctUntilChanged())
            .subscribe((term: string) => this.getAutocompleteEscalate(term));

        this.notificationToChange.pipe(debounceTime(delay), distinctUntilChanged())
            .subscribe((term: string) => this.getAutocompleteNotify(term));

        this.groupOwnerChange.pipe(debounceTime(delay), distinctUntilChanged(),
            switchMap((term: string) => {
                return (term && term.length > 1) ? this.individualsService.getIndividuals(term) : of([]);
            }))
            .subscribe(res => this.autocompleteGroupOwners = res);
    }

    /**
     * Add Individual / Device search onChange event handler to trigger Subject
     */
    onGroupChanged(event: any) {
        if (event.data === undefined) {
            this.groupSearch = event;
            this.groupChange.next(event);
        }
    }

    /**
     * Add Individual / Device search onChange event handler to trigger Subject
     */
    onEscalateToChanged($event: any) {
        if (typeof $event === 'string' || $event instanceof String) {
            this.selectedGroup.escalate_to = this.currentEscalation;
            this.escalateValid = true;
            this.escalateTimeValid = true;
            this.reminderTimeValid = true;
            this.saveButtonDisabled = false;
            this.escalateToChange.next($event.toString());
        }
    }

    escalateTimeChange() {
        this.escalateTimeValid = true;
        this.saveButtonDisabled = false;
    }

    reminderTimeChange() {
        this.reminderTimeValid = true;
        this.saveButtonDisabled = false;
    }

    ackRequiredChange() {
        this.ackRequiredValid = true;
        this.saveButtonDisabled = false;
    }

    onGroupOwnerChanged($event: any) {
        this.groupOwnerChange.next($event);
    }

    onNotificationToChanged($event: any) {
        if (typeof $event === 'string' || $event instanceof String) {
            this.notificationToChange.next($event.toString());
        }
    }

    /**
     * Actual Search call.
     * Get Group names for autocomplete
     */
    getAutocompleteGroups(identifier: string) {
        if (typeof identifier != 'undefined' && identifier.length > 1 && identifier) {
            let groupTypeId = this.getGroupTypeId(this.autoComplete.filterControl.value);
            this.filter = identifier;
            if (groupTypeId != 0) {
                this.groupsService.getGroupNamesAndGroupTypeId(identifier, groupTypeId.toString(), Activity.ALL).subscribe((groups: Group[]) => {
                    this.autocompleteGroups = groups;
                    this.autocompleteGroupsFormatted = groups.map((group) => group.name);
                });
            } else {
                this.groupsService.getGroupNames(identifier, Activity.ALL).subscribe((groups: Group[]) => {
                    this.autocompleteGroups = groups;
                    this.autocompleteGroupsFormatted = groups.map((group) => group.name);
                });
            }
        } else {
            this.autocompleteGroups = [];
            this.autocompleteGroupsFormatted = [];
        }
    }

    load(page: any) {
        if (this.filter ? page.data === undefined : false) {
            let groupTypeId = this.getGroupTypeId(this.autoComplete.filterControl.value);
            this.pageNum = page;
            if (groupTypeId != 0) {
                this.groupsService.getGroupNamesPageAndGroupTypeId(this.filter, page * 15, groupTypeId.toString(), Activity.ALL).subscribe((groups: Pageable<Group>) => {
                    this.autocompleteGroups = groups.content;
                    this.autocompleteGroupsFormatted = groups.content.map(group => group.name);
                });
            } else {
                this.groupsService.getGroupNamesPage(this.filter, page * 15, Activity.ALL).subscribe((groups: Pageable<Group>) => {
                    this.autocompleteGroups = groups.content;
                    this.autocompleteGroupsFormatted = groups.content.map(group => group.name);
                });
            }
        }
    }

    updateGroupType() {
        for (let gType of this.groupTypes) {
            if (gType.group_type_id === this.selectedGroup.group_type_id) {
                if (this.groupTypeUsesSystemOverride || (this.selectedGroup.escalate_time_limit === this.defaultEscalationInterval
                        && this.selectedGroup.reminder_time_limit === this.defaultReminderInterval)) {
                    this.groupTypeUsesSystemOverride = gType.use_system_global_intervals;
                } else if (gType.use_system_global_intervals) {
                    this.isProcessing = true;
                    const popupDialog = this.dialog.open(GroupTypeWarningDialogComponent, {
                        width: '510px', height: '225px', panelClass: 'confirmation', data: gType.group_type
                    });

                    popupDialog.afterClosed().subscribe(result => {
                        if (result) {
                            this.groupTypeUsesSystemOverride = true;
                            this.selectedGroup.escalate_time_limit = this.defaultEscalationInterval;
                            this.selectedGroup.reminder_time_limit = this.defaultReminderInterval;
                            this.prevGroupTypeId = this.selectedGroup.group_type_id;
                        } else {
                            this.selectedGroup.group_type_id = this.prevGroupTypeId;
                        }
                        this.isProcessing = false;
                    });
                }
                break;
            }
        }
        this.isValid();
    }

    updateEscalateTo(event: any) {
        this.escalateToChipList = [];
        this.autocompleteEscalate = [];
        this.currentEscalation = '';
        this.groupCheckedRadioEsc = event.value;
        if (event.value === ApplicationConstant.doNotEscalate) {
            this.selectedGroup.escalate_to = ApplicationConstant.doNotEscalate;
            this.selectedGroup.escalate_to_id = null;
            this.escalateToChipList = [this.selectedGroup.escalate_to];
            this.escalateValid = true;
            this.escalateTimeValid = true;
            this.selectedGroup.escalate_time_limit = this.defaultEscalationInterval;
            this.selectedGroup.reminder_time_limit = this.defaultReminderInterval;
        }
        this.saveButtonDisabled = false;
    }

    updateNotificationTo(event: any) {
        this.notificationToChange.next('');
        this.notificationToChipList = [];
        this.autocompleteNotificationTo = [];
        this.currentNotificationTo = '';
        this.groupCheckedRadioNotif = event.value;
        if (event.value === ApplicationConstant.doNotNotify) {
            this.selectedGroup.notification_to_id = undefined;
            this.notificationToChipList = [];
        }
    }

    getAutocompleteEscalate(term: string) {
        this.autocompleteEscalate = [];
        if (term.trim().length >= 2) {
            switch (this.escalationRadioButton) {
                case ApplicationConstant.group:
                    this.groupsService.getGroupNames(term).subscribe(options => this.autocompleteEscalate = options);
                    this.displayId = false;
                    break;
                case ApplicationConstant.individual:
                    this.individualsService.getIndividualsAndDevices(term).subscribe(individualsAndDevices => this.autocompleteEscalate = individualsAndDevices.content);
                    this.displayId = true;
                    break;
                case ApplicationConstant.doNotEscalate:
                    this.selectedGroup.escalate_to_id = '';
                    this.selectedGroup.escalate_to_type = ApplicationConstant.doNotEscalate;
                    if (this.selectedGroup.reminder_time_limit === 0) {
                        this.selectedGroup.reminder_time_limit = this.defaultReminderInterval;
                    }
                    if (this.selectedGroup.escalate_time_limit === 0) {
                        this.selectedGroup.escalate_time_limit = this.defaultEscalationInterval;
                    }
                    this.escalateToChipList = [this.selectedGroup.escalate_to];
                    break;
                default:
                    this.groupCheckedRadioEsc = this.escalationRadioButton;
                    break;
            }
        }
    }

    getAutocompleteNotify(term: string) {
        this.autocompleteNotificationTo = [];
        this.saveButtonDisabled = false;
        if (term.trim().length >= 2) {
            switch (this.notificationRadioButton) {
                case ApplicationConstant.group:
                    this.groupsService.getGroupNames(term).subscribe(options => this.autocompleteNotificationTo = options);
                    this.displayId = false;
                    break;
                case ApplicationConstant.individual:
                    this.individualsService.getIndividualsAndDevices(term).subscribe(individualsAndDevices => this.autocompleteNotificationTo = individualsAndDevices.content);
                    this.displayId = true;
                    break;
                case ApplicationConstant.doNotNotify:
                    this.selectedGroup.notification_to_id = undefined;
                    this.notificationToChipList = [];
                    break;
                default:
                    this.groupCheckedRadioEsc = this.escalationRadioButton;
                    break;
            }
        }
    }

    selectGroupEscalate(escalateData: any) {
        if (escalateData.device_name === this.selectedGroup.device_name) {
            this.snackbarService.openSnackBar('Cannot set a group as it\'s own escalate', true);
            this.escalateToChipList.filter(e => e.device_name === this.selectedGroup.device_name);
            this.autocompleteEscalate = [];
            this.selectedGroup.escalate_to = '';
            this.currentEscalation = '';
            this.escalateToChange.next('');
        } else {
            if (this.escalateToChipList.length < 1) {
                this.selectedGroup.escalate_to = escalateData.full_name || escalateData.name;
                this.selectedGroup.escalate_to_id = escalateData.id;
                this.selectedGroup.escalate_to_type = (this.groupCheckedRadioEsc === ApplicationConstant.group ? ApplicationConstant.group : ((escalateData.external_id !== undefined) ? ApplicationConstant.individual : ApplicationConstant.device));
                this.escalateToChipList.push(this.selectedGroup.escalate_to);
            } else {
                this.snackbarService.openSnackBar('Cannot add more than one contact for escalation.', true);
            }
            this.autocompleteEscalate = [];
            this.selectedGroup.escalate_to = '';
            this.currentEscalation = '';
            this.escalateToChange.next('');
        }
    }

    selectGroupNotify(notifyData: any) {
        this.notificationToChipList[0] = notifyData.full_name || notifyData.name;
        this.selectedGroup.notification_to_id = notifyData.id;
        this.selectedGroup.notification_to_type = (this.groupCheckedRadioNotif === ApplicationConstant.group ? ApplicationConstant.group : ((notifyData.external_id !== undefined) ? ApplicationConstant.individual : ApplicationConstant.device));
        this.autocompleteNotificationTo = [];
        this.currentNotificationTo = '';
        this.notificationToChange.next('');
        this.saveButtonDisabled = false;
    }

    groupOwnerSelect(event: any, ele: HTMLInputElement): void {
        this.addGroupOwner(event.option.value);
        this.saveButtonDisabled = false;
        ele.value = '';
    }

    addGroupOwner(owner: Individual): void {
        if (!this.selectedGroup.owners.find(p => p.id === owner.id)) {
            this.selectedGroup.owners.push(owner);
        } else {
            this.snackbarService.openSnackBar(`${owner.full_name} is already a group owner`, true);
            this.currentGroupOwner = '';
        }
        this.onGroupOwnerChanged('');
    }

    changeOnCall() {
        let text = this.currentUser.full_name + ' will take the on call immediately';
        if (this.selectedGroup.sub_group_enabled) {
            text = text + ' and the group membership type will be changed';
        }
        const popupDialog = this.dialog.open(DeleteGroupsDialogComponent, {
            width: '425px', height: '225px', panelClass: 'confirmation', data: {
                dataKey: this.selectedGroup.name, dialogTitle: text + '. Are you sure?'
            }
        });

        popupDialog.afterClosed().subscribe(result => {
            if (result) {
                this.isProcessing = true;
                this.ready = false;

                if (this.currentUserInfo) {
                    this.dataService.getIndividualAndAssociateNames(this.currentUserInfo.email).subscribe(res => {
                        // @ts-ignore
                        this.groupsService.addGroupMember(this.selectedGroup.id, res.content[0].id, true).subscribe({
                            next: () => {
                                this.check();
                                this.groupsService.getGroup(this.selectedGroup.id).subscribe(this.populateGroup.bind(this));
                                this.snackbarService.openSnackBar('Added user as On Call', false);
                            }, error: error => this.dataFetchError('Could not add user as On Call ' + error.error.message)
                        });
                    });
                } else {
                    this.snackbarService.openSnackBar('Could not add user as On Call', true);
                }
            }
            this.isProcessing = false;
            this.ready = true;
        });
    }

    removeGroupOwner(owner: Individual) {
        if (this.selectedGroup.owners.length === 1) {
            this.snackbarService.openSnackBar(`${owner.full_name} is your only owner, you cannot delete them`, true);
        } else {
            this.saveButtonDisabled = false;
            this.selectedGroup.owners = this.selectedGroup.owners.filter(p => p.id !== owner.id);
        }
    }

    removeEscalateChip(value: any) {
        this.escalateToChipList = this.escalateToChipList.filter(e => e.id !== value.id);
    }

    removeNotifyChip(value: any) {
        this.notificationToChipList = this.notificationToChipList.filter(e => e.id !== value.id);
        this.saveButtonDisabled = false;
    }

    cancel() {
        this.onGroupChanged('');
        this.escalateToChipList = [];
        this.selectedGroup = null;
        this.currentGroupOwner = '';
        this.autocompleteGroups = [];
        this.autocompleteGroupsFormatted = [];
        this.autocompleteGroupOwners = [];
        this.isProcessing = false;
        this.showDetailsPage = false;
        this.showSpinner = false;
        this.saveButtonDisabled = true;
        this.getGroupsForGrid();
    }

    openGroup(group: Group) {
        this.resetVariablesOnSelect();
        this.groupsService.getGroup(group.id).subscribe({
            next: this.populateGroup.bind(this),
            error: () => {
                this.snackbarService.openSnackBar('Error retrieving group from server, try again later. If this persists, contact an admin', true);
                this.ready = true;
                this.isProcessing = false;
                this.showSpinner = false;
                return;
            }
        });
    }

    resetVariablesOnSelect() {
        this.ready = false;
        this.isProcessing = true;
        this.showDetailsPage = false;
        this.showSpinner = true;
        this.currentGroupOwner = '';
        this.members = [];
        this.groupChange.next('');
        this.saveButtonDisabled = true;
        this.onGroupChanged('');
    }

    filterChange(value: boolean) {
        this.onlyShowOwnedGroups = value;
        let old = this.oldPage;
        this.oldPage = this.page.offset;
        this.page.offset = old;
        this.getGroupsForGrid();
    }

    getGroupsForGrid() {
        if (this.unregisteredUser) {
            this.onlyShowOwnedGroups = false;
            this.showGroupFilter = false;
            this.groupsService.getAllGroupsPage(this.page).subscribe((res: Pageable<Group>) => {
                this.page.count = res.total_elements;
                this.groupList = res.content;
            });
        } else {
            this.groupsService.getGroupsByOwnerIdPage(this.currentUserInfo.individual_id, this.page, "all").subscribe((res: Pageable<Group>) => {
                if (res.total_elements !== 0) {
                    this.showGroupFilter = true;

                    if (this.onlyShowOwnedGroups) {
                        this.page.count = res.total_elements;
                        this.groupList = res.content;
                    }
                } else {
                    this.onlyShowOwnedGroups = false;
                    this.showGroupFilter = false;
                }
            }).add(() => {
                if (!this.onlyShowOwnedGroups) {
                    this.groupsService.getAllGroupsPage(this.page).subscribe((res: Pageable<Group>) => {
                        this.page.count = res.total_elements;
                        this.groupList = res.content;
                    });
                }
            });
        }
    }

    onActivate(event) {
        if (event.type == 'click') {
            this.openGroup(event.row);
        }
    }

    pageCallback(pageInfo: { count?: number, pageSize?: number, limit?: number, offset?: number }) {
        if (!this.showSpinner) {
            this.page.offset = pageInfo.offset;
            this.getGroupsForGrid();
        }
    }

    sortCallback(sortInfo: { sorts: { dir: string, prop: string }[], column: {}, prevValue: string, newValue: string }) {
        if (!this.showSpinner) {
            this.page.orderDir = sortInfo.sorts[0].dir;
            this.page.orderBy = sortInfo.sorts[0].prop;

            this.getGroupsForGrid();
        }
    }

    dataFetchError(errorMsg: string) {
        this.snackbarService.openSnackBar(errorMsg, true);
        this.isProcessing = false;
        this.hourglass = false;
    }

    isEditable() {
        return ((this.isAdmin || this.isOwner) && (this.selectedGroup.id == null || this.selectedGroup.active));
    }

    isSaveButtonDisabled() {
        return this.saveButtonDisabled || !this.isGroupFormPopulated();
    }

    isGroupFormPopulated() {
        let validName = true;
        if(this.selectedGroup.name != null) {
            if(this.selectedGroup.name.length == 0) {
                validName = false;
            }
        } else {
            validName = false;
        }
        return validName && this.selectedGroup.group_type_id != null && this.selectedGroup.members.length != 0;
    }

    getGroupTypeName(id: number) {
        if (id) {
            return this.groupTypes.find(gType => gType.group_type_id === id).group_type;
        }
        return this.groupTypes.find(gType => gType.group_type_id === 1).group_type;
    }

    openPopup(event: any) {
        const memberType = (event.member_type ? event.member_type : (event.recipient_type ? event.recipient_type : null));
        switch (memberType) {
            case 'INDIVIDUAL':
                this.openIndividualPopup((event.member_type ? event.member_id : (event.recipient_type ? event.recipient_id : null)));
                break;
            case 'DEVICE':
                this.openDevicePopup((event.member_type ? event.member_id : (event.recipient_type ? event.recipient_id : null)));
                break;
            case 'GROUP':
                this.openGroupPopup((event.member_type ? event.member_id : (event.recipient_type ? event.recipient_id : null)));
                break;
            default:
                if (event.full_name != undefined) {
                    this.openIndividualPopup(event.id);
                } else {
                    let popupOpened = false;
                    this.individualsService.getIndividuals(event).subscribe({
                        next: individuals => {
                            const individuals2 = individuals.filter(individual => individual.full_name === event);
                            if (individuals2.length === 1) {
                                this.openIndividualPopup(individuals2[0].id);
                                popupOpened = true;
                            } else {
                                this.groupsService.getGroupNames(event).subscribe({
                                    next: groups => {
                                        groups = groups.filter(group => group.name === event);
                                        if (groups.length === 1) {
                                            this.openGroupPopup(groups[0].id);
                                            popupOpened = true;
                                        } else {
                                            this.deviceService.getDevicesByName(event).subscribe({
                                                next: devices => {
                                                    devices = devices.filter(device => device.name === event);
                                                    if (devices.length === 1) {
                                                        this.openDevicePopup(devices[0].id);
                                                        popupOpened = true;
                                                    }
                                                    if (!popupOpened) {
                                                        this.snackbarService.openSnackBar('Individual/Channel/Group Not Found', true);
                                                    }
                                                }, error: () => {
                                                    this.dataFetchError('Error fetching channel');
                                                }
                                            });
                                        }
                                    }, error: () => {
                                        this.dataFetchError('Error fetching group');
                                    }
                                });
                            }
                        }, error: () => {
                            this.dataFetchError('Error fetching individual');
                        }
                    });
                }
                break;
        }
    }

    openDevicePopup(deviceID: any) {
        this.deviceService.getDevice(deviceID).subscribe((device: Device) => {
            this.dialog.open(ChipPopupComponent, {
                width: '55%', maxHeight: '90%', data: {object: device, type: 'DEVICE'}
            });
        });
    }

    openIndividualPopup(indID: any) {
        this.individualsService.getIndividual(indID).subscribe((individual: Individual) => {
            this.dialog.open(ChipPopupComponent, {
                width: '65%', maxHeight: '90%', data: {object: individual, type: 'INDIVIDUAL'}
            });
        });
    }

    openGroupPopup(groupID: any) {
        this.groupsService.getGroup(groupID).subscribe((group: Group) => {
            this.dialog.open(ChipPopupComponent, {
                width: '85%', maxHeight: '90%', data: {object: group, type: 'GROUP'}
            });
        });
    }

    openGroupEscalationFAQ() {
        this.dialog.open(FaqComponent, {
            width: '95%',
            height: '95%',
            data: {
                category: 'Send Message',
                question: 'How does group escalation work?'
            }
        });
    }

    openGroupEditFAQ() {
        this.dialog.open(FaqComponent, {
            width: '95%',
            height: '95%',
            data: {
                category: 'Groups',
                question: 'How do I create, update, or delete a group?'
            }
        });
    }

    updateGroupName() {
        this.selectedGroup.generateDeviceName();
        this.saveButtonDisabled = false;
    }

    getSaveButtonTooltip(): string {
        let missing: string[] = [];
        if (this.selectedGroup.name == null) {
            missing.push('Group Name');
        }
        if (this.selectedGroup.group_type_id == null) {
            missing.push('Group Type');
        }
        if (this.selectedGroup.members.length == 0) {
            missing.push('Recipients');
        }
        if (missing.length == 0) {
            return null;
        }
        return `Missing required fields: ${missing.join(', ')}`;
    }

    public getNotify(): void {
        switch (this.selectedGroup.notification_to_type) {
            case 'DEVICE' :
                this.deviceService.getDevice(parseInt(this.selectedGroup.notification_to_id)).subscribe(device => this.notificationToChipList[0] = device.name);
                break;
            case 'GROUP' :
                this.groupsService.getGroup(parseInt(this.selectedGroup.notification_to_id)).subscribe(group => this.notificationToChipList[0] = group.name);
                break;
            case 'INDIVIDUAL' :
                this.individualsService.getIndividual(parseInt(this.selectedGroup.notification_to_id)).subscribe(individual => this.notificationToChipList[0] = individual.full_name);
                break;
            case undefined:
            case null:
            case '':
                break;
            default:
                this.snackbarService.openSnackBar('Error parsing notify to, check data and try again');
        }
    }

    private getGroupTypeId(name: string) {
        if (name.toUpperCase() === 'ALL') {
            return 0;
        }
        return this.groupTypes.find(gType => gType.group_type.toUpperCase() === name.toUpperCase()).group_type_id;
    }

    private addGroup(group: Group) {
        const tempReturnValue = group.name;
        this.dataService.getDeviceNameAvailability(group.name).subscribe((isValid) => {
            if (isValid) {
                this.doGroupUpdates(group, 'createGroup', 'Group Added Successfully');
            } else {
                this.snackbarService.openSnackBar(`Cannot create group: ${tempReturnValue}, same name as existing device.`, true);
            }
        });
    }

    private updateGroup(group: Group) {
        this.doGroupUpdates(group, 'updateGroup', 'Group Updated Successfully');
    }

    /**
     * Enhance GroupMembers by dynamically adding Recipient like properties so member can be used like a Recipient.
     */
    private groupMembersToRecipients() {
        this.selectedGroup.members.forEach(member => {
            member['recipient_id'] = member.member_id;
            member['recipient_type'] = member.member_type;
        });
    }

    /**
     * Enhance Recipients by dynamically adding GroupMember like properties so Recipient can be used like a GroupMember.
     */
    private recipientsToGroupMembers() {
        this.selectedGroup.members.forEach(member => {
            member.member_id = parseInt(member['recipient_id'], 0);
            member.member_type = member['recipient_type'];
        });
    }

    setSelfSubToFalse() {
        this.selectedGroup.open_group = false;
    }

    isAckNotRequired(selectedGroup) {
        return !selectedGroup.ack_required;
    }
}
